import React, { FC } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    ColumnDef,
    flexRender,
    TableOptions,
    TableState,
    getFilteredRowModel,
    getExpandedRowModel,
    SortingState,
} from '@tanstack/react-table';
import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import classNames from 'classnames';

import { TablePagination } from './components/TablePagination';
import { TableCardList } from './components/TableCardList';
import { TableSortSelect } from './components/TableSortSelect';
import { useStyles } from './Table.styles';
import { ThemeProps } from '@styles/theme';
import { useBreakpointDown } from '@shared/hooks/useBreakpoint';
import { Fade, Skeleton } from '@mui/material';

export type PaginationProps = {
    currentPage: number;
    limit: number;
    isFirstPage: boolean;
    isLastPage: boolean;
    totalEntities: number;
    totalPages: number;
};

type Props = {
    renderRowSubComponent?: Function;
    className?: string;
    isLoading?: boolean;
    useSkeleton?: boolean;
    paginationDisabled?: boolean;
    paginationStatus: PaginationProps;
    initialState?: Partial<TableState>;
    fetchData?: (...args: any) => void;
    getRowClassName?: (...args: any) => string | undefined;
    getRowOnClick?: (...args: any) => void;
};

type RowData = {
    isDisabled?: boolean;
};

type TableProps<T extends object = {}> = TableOptions<T>;

type Table<T extends RowData> = FC<Props & TableProps<T>>;

type TableParams<T> = {
    columns: ColumnDef<T>[];
    data: T[];
    renderRowSubComponent?: Function;
    className?: string;
    isLoading?: boolean;
    useSkeleton?: boolean;
    paginationDisabled?: boolean;
    paginationStatus: PaginationProps;
    initialState?: Partial<TableState>;
    fetchData: Function;
    getRowClassName?: (...args: any) => string | undefined;
    getRowOnClick?: (...args: any) => void;
};

const DEFAULT_ROW_HEIGHT = 50;
const DEFAULT_SCROLL_TABLE_ROW_HEIGHT = 56;

export function Table<T extends object = {}>(tableProps: TableParams<T>) {
    const {
        columns,
        data,
        renderRowSubComponent,
        className,
        isLoading,
        useSkeleton,
        paginationDisabled,
        paginationStatus,
        initialState,
        fetchData = () => {},
        getRowClassName,
        getRowOnClick,
    } = tableProps;

    const [sorting, setSorting] = React.useState<SortingState>(
        initialState?.sorting || []
    );

    const instance = useReactTable({
        columns,
        data,
        autoResetAll: true,
        manualPagination: true,
        manualExpanding: false,
        manualSorting: true,
        enableMultiSort: true,
        enableSortingRemoval: true,
        state: { ...initialState, sorting: sorting },
        getRowCanExpand: () => renderRowSubComponent !== undefined,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        onSortingChange: setSorting,
    });

    const {
        getHeaderGroups,
        getRowModel,
        initialState: { pagination },
    } = instance;

    const { pageIndex, pageSize } = pagination;

    React.useEffect(() => {
        fetchData({ pageIndex, pageSize, sortBy: sorting });
    }, [pageIndex, pageSize, sorting]);

    const goToPage = (pageIndex: number) => fetchData({ pageIndex });

    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const isCardLayout = useBreakpointDown('mobile');

    const min = (a: number, b: number) => (a > 0 && a < b ? a : b);

    if (isCardLayout) {
        return (
            <div
                className={classNames(styles.table, className, {
                    [styles.tableDisabled]: isLoading,
                    [styles.scrollTable]: paginationDisabled,
                    [styles.normalTable]: !paginationDisabled,
                    [styles.tableCardFlow]: !paginationDisabled,
                    [styles.tableCardScroll]: paginationDisabled,
                })}
            >
                <div
                    className={classNames(styles.mainContainer, {
                        [styles.mainContainerCardFlow]: !paginationDisabled,
                    })}
                >
                    <TableSortSelect
                        instance={instance}
                        isLoading={isLoading && !useSkeleton}
                    />
                    <TableCardList
                        rows={getRowModel().rows}
                        isLoading={isLoading}
                        useSkeleton={useSkeleton}
                        skeletonCount={min(data?.length ?? pageSize, pageSize)!}
                        scrollMode={paginationDisabled}
                        renderRowSubComponent={renderRowSubComponent}
                        getRowClassName={getRowClassName}
                        getRowOnClick={getRowOnClick}
                    />
                </div>
                {!paginationDisabled && (
                    <TablePagination
                        paginationStatus={paginationStatus}
                        goToPage={goToPage}
                    />
                )}
            </div>
        );
    }

    return (
        <div
            className={classNames(styles.table, className, {
                [styles.tableDisabled]: isLoading,
                [styles.scrollTable]: paginationDisabled,
                [styles.normalTable]: !paginationDisabled,
            })}
        >
            <div
                className={classNames(styles.mainContainer, className, {
                    [styles.scrollTable]: paginationDisabled,
                    [styles.normalTable]: !paginationDisabled,
                })}
            >
                <div className={classNames(styles.header, className, {
                    [styles.scrollTable]: paginationDisabled,
                    [styles.normalTable]: !paginationDisabled,
                })}>
                    {isLoading && !useSkeleton && (
                        <div className={styles.loadingWrapper}>
                            <CircularProgress
                                className={styles.loader}
                                color="inherit"
                                size={24}
                            />
                        </div>
                    )}

                    {getHeaderGroups().map(g =>
                        g.headers.map(h => {
                            const { minSize, size, maxSize } = {
                                ...h.column.columnDef,
                                size: h.getSize(),
                            };

                            return (
                                <div
                                    key={h.column.id}
                                    className={classNames(styles.headerCell, {
                                        [styles.withSorting]:
                                            h.column.getCanSort(),
                                    })}
                                    style={{
                                        minWidth: minSize,
                                        maxWidth: maxSize || 'none',
                                        flexGrow: size || 1,
                                        flexShrink: 1,
                                        flexBasis: 0,
                                    }}
                                    onClick={h.column.getToggleSortingHandler()}
                                >
                                    <span style={{
                                        fontWeight: 'bold',
                                    }}>
                                        {flexRender(
                                            h.column.columnDef.header,
                                            h.getContext()
                                        )}
                                    </span>

                                    {h.column.getCanSort() &&
                                        !h.column.getIsSorted() && (
                                        <ArrowUpwardOutlinedIcon
                                            className={
                                                styles.sortPlaceholder
                                            }
                                        />
                                    )}

                                    {h.column.getCanSort() &&
                                        h.column.getIsSorted() && (
                                        <div
                                            className={styles.sortIndicator}
                                        >
                                            <ArrowUpwardOutlinedIcon
                                                className={classNames(
                                                    styles.sortIcon,
                                                    {
                                                        [styles.sortedDesc]:
                                                                sorting[0].desc,
                                                    }
                                                )}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                <div
                    className={classNames(styles.body, className, {
                        [styles.scrollTable]: paginationDisabled,
                        [styles.normalTable]: !paginationDisabled,
                    })}
                >
                    {isLoading && useSkeleton
                        ? Array.from(
                            {
                                length: min(
                                    data?.length ?? pageSize,
                                    pageSize
                                )!,
                            },
                            () => ({})
                        ).map((_, rowIndex) => {
                            return (
                                <Fade
                                    key={'fade_' + rowIndex}
                                    in={true}
                                    timeout={1000}
                                    //   style={{height: !paginationDisabled ? `${100/paginationStatus.limit}%` : '10%'}}
                                >
                                    <div
                                        key={rowIndex}
                                        className={classNames(styles.row)}
                                        style={{
                                            height: !paginationDisabled ? `${100/paginationStatus.limit}%` : 'auto',
                                            minHeight: paginationDisabled ? DEFAULT_SCROLL_TABLE_ROW_HEIGHT : DEFAULT_ROW_HEIGHT,
                                        }}
                                    >
                                        <div
                                            className={
                                                styles.defaultRowContent
                                            }
                                        >
                                            {getHeaderGroups().map(g =>
                                                g.headers.map(cell => {
                                                    const {
                                                        minSize,
                                                        size,
                                                        maxSize,
                                                    } = {
                                                        ...cell.column
                                                            .columnDef,
                                                        size: cell.getSize(),
                                                    };
                                                    const skeletonWidth =
                                                          size;

                                                    return (
                                                        <div
                                                            className={
                                                                styles.cell
                                                            }
                                                            key={cell.id}
                                                            style={{
                                                                minWidth:
                                                                      minSize,
                                                                maxWidth:
                                                                      maxSize ||
                                                                      'none',
                                                                flexGrow:
                                                                      size || 1,
                                                                flexShrink: 1,
                                                                flexBasis: 0,
                                                            }}
                                                        >
                                                            <Skeleton
                                                                variant="rectangular"
                                                                width={
                                                                    skeletonWidth
                                                                }
                                                            />
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                </Fade>
                            );
                        })
                        : getRowModel().rows.map(row => {
                            const rowData = row.original as RowData;

                            return (
                                <Fade
                                    key={row.id}
                                    in={true}
                                    timeout={1000}
                                >
                                    <div
                                        key={row.id}
                                        className={classNames(
                                            styles.row,
                                            getRowClassName?.(row.original, row),
                                            rowData && rowData.isDisabled
                                                ? 'disabled'
                                                : ''
                                        )}
                                        onClick={() => getRowOnClick?.(row.original, row)}
                                        style={{
                                            height: !paginationDisabled ? (row.getIsExpanded() ? '' : `${100/paginationStatus.limit}%`) : 'auto',
                                            minHeight: paginationDisabled ? DEFAULT_SCROLL_TABLE_ROW_HEIGHT : DEFAULT_ROW_HEIGHT,
                                        }}
                                    >
                                        <div
                                            className={
                                                styles.defaultRowContent
                                            }
                                            style={{
                                                height: !paginationDisabled ? (row.getIsExpanded() ? '22%' : `100%`) : 'auto',
                                                minHeight: paginationDisabled ? DEFAULT_SCROLL_TABLE_ROW_HEIGHT : DEFAULT_ROW_HEIGHT,
                                            }}
                                        >
                                            {row
                                                .getVisibleCells()
                                                .map(cell => {
                                                    const disableEllipsis = Boolean(
                                                        (cell.column.columnDef as any).meta
                                                            ?.disableEllipsis
                                                    );
                                                    const {
                                                        minSize,
                                                        size,
                                                        maxSize,
                                                    } = {
                                                        ...cell.column
                                                            .columnDef,
                                                        size: cell.column.getSize(),
                                                    };

                                                    return (
                                                        <div
                                                            className={
                                                                styles.cell
                                                            }
                                                            key={cell.id}
                                                            style={{
                                                                minWidth:
                                                                      minSize,
                                                                maxWidth:
                                                                      maxSize ||
                                                                      'none',
                                                                flexGrow:
                                                                      size || 1,
                                                                flexShrink: 1,
                                                                flexBasis: 0,
                                                            }}
                                                        >
                                                            <span
                                                                className={classNames(
                                                                    {
                                                                        [styles.ellipsis]:
                                                                              !disableEllipsis,
                                                                        [styles.disabledEllipsis]:
                                                                              disableEllipsis,
                                                                    }
                                                                )}
                                                            >
                                                                {flexRender(
                                                                    cell
                                                                        .column
                                                                        .columnDef
                                                                        .cell,
                                                                    cell.getContext()
                                                                )}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                        {renderRowSubComponent &&
                                              row.getIsExpanded() && (
                                            <div
                                                className={
                                                    styles.expandedRowContent
                                                }
                                                style={{
                                                    height: paginationDisabled ? 'auto' : '78%',
                                                }}
                                            >
                                                {renderRowSubComponent({
                                                    row,
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </Fade>
                            );
                        })}
                </div>
            </div>
            {!paginationDisabled && (
                <TablePagination
                    paginationStatus={paginationStatus}
                    goToPage={goToPage}
                />
            )}
        </div>
    );
}
